import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { HttpStatusCode, isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "@/hooks/useApiClient";
import { postAiStoredPicturesDownload } from "../api";
import {
  buildAiDownloadPreparation,
  type AiDownloadPreparation,
} from "../downloadCondition";
import { type AiStoredPictureResult } from "../types";

interface UseAiStoredPicturesDownloadResult {
  isResultsOpen: boolean;
  selectedIds: Set<number>;
  downloadPreparation: AiDownloadPreparation | null;
  isDownloadOpen: boolean;
  isDownloading: boolean;
  downloadError: string;
  preparationError: string;
  setSelectedIds: Dispatch<SetStateAction<Set<number>>>;
  openResults: () => void;
  closeResults: () => void;
  openDownloadConfirmation: () => void;
  closeDownload: () => void;
  downloadSelectedResults: () => Promise<void>;
}

const downloadErrorMessage = (error: unknown): string => {
  if (!isAxiosError(error)) {
    return "登記の取得に失敗しました";
  }
  if (error.code === "ECONNABORTED") {
    return "処理結果を確認できませんでした。取得状況一覧を確認してください";
  }
  if (error.response?.status === HttpStatusCode.BadRequest) {
    return "登記は取得されませんでした。もう一度検索してください";
  }
  if (error.response?.status === HttpStatusCode.Forbidden) {
    return "このアカウントでは登記を取得できません";
  }
  return "登記の取得に失敗しました";
};

export const useAiStoredPicturesDownload = (
  results: AiStoredPictureResult[] | undefined
): UseAiStoredPicturesDownloadResult => {
  const { apiClient } = useApiClient();
  const navigate = useNavigate();
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [downloadPreparation, setDownloadPreparation] =
    useState<AiDownloadPreparation | null>(null);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [preparationError, setPreparationError] = useState("");

  const selectedResults = useMemo(
    () =>
      (results ?? []).filter((result) =>
        selectedIds.has(result.pictureRequestDetailId)
      ),
    [results, selectedIds]
  );

  useEffect(() => {
    setIsResultsOpen(false);
    setSelectedIds(new Set());
    setDownloadPreparation(null);
    setIsDownloadOpen(false);
    setIsDownloading(false);
    setDownloadError("");
    setPreparationError("");
  }, [results]);

  const openDownloadConfirmation = (): void => {
    const preparation = buildAiDownloadPreparation(selectedResults);
    if (!preparation.ok) {
      setPreparationError(
        "取得に必要な情報を確認できませんでした。もう一度検索してください"
      );
      setIsResultsOpen(false);
      return;
    }
    setDownloadPreparation(preparation.value);
    setDownloadError("");
    setPreparationError("");
    setIsResultsOpen(false);
    setIsDownloadOpen(true);
  };

  const downloadSelectedResults = async (): Promise<void> => {
    if (downloadPreparation === null || isDownloading) {
      return;
    }
    setIsDownloading(true);
    setDownloadError("");
    try {
      const pictureRequestId = await postAiStoredPicturesDownload(
        apiClient,
        downloadPreparation.condition
      );
      navigate(`/acquirebookstatus/${pictureRequestId}/`);
    } catch (error) {
      setDownloadError(downloadErrorMessage(error));
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    isResultsOpen,
    selectedIds,
    downloadPreparation,
    isDownloadOpen,
    isDownloading,
    downloadError,
    preparationError,
    setSelectedIds,
    openResults: () => {
      setIsResultsOpen(true);
    },
    closeResults: () => {
      setIsResultsOpen(false);
    },
    openDownloadConfirmation,
    closeDownload: () => {
      setIsDownloadOpen(false);
      setIsResultsOpen(true);
    },
    downloadSelectedResults,
  };
};
